'use client';

import { c as _c } from "react/compiler-runtime";
import type { FocusCodeResult } from '@/app/(chat)/api/chat/chat-tools/tools';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useEventCallback } from 'usehooks-ts';
import { completedFocusCodeAutoNavigateKeys } from './page-action-replays';
import { getCandidateDiffFileElementIds } from '@/lib/stores/navigation/diff-file-element-ids';
import { useDiffNavigationStore } from '@/lib/stores/navigation/diff-navigation-store';
import { usePrTabNavigationStore } from '@/lib/stores/pr-tab-navigation/tab-navigation-store';
import { PR_TABS } from '@/lib/stores/pr-tab-navigation/types';
import { clearHighlights, highlightLine, highlightLines } from '@/lib/utils/line-highlighting';

// Constants for better readability
const NAVBAR_OFFSET = 72;
const TAB_SWITCH_DELAY = 300;
const LINE_HIGHLIGHT_DELAY_WITH_TAB = 500;
const LINE_HIGHLIGHT_DELAY_NO_TAB = 100;
const NAVIGATION_RETRY_MAX_ATTEMPTS = 25;
const NAVIGATION_RETRY_BASE_DELAY = 100;
const NAVIGATION_RETRY_MAX_DELAY = 500;
const LINE_SCROLL_RETRY_MAX_ATTEMPTS = 15;
const LINE_SCROLL_RETRY_DELAY = 80;
const MAX_EXPANDED_LINE_RANGE = 500;
type FocusCodeSide = 'old' | 'new';
type FocusCodeBlockProps = {
  args: FocusCodeResult;
  disableAutoNavigate?: boolean;
  autoNavigateKey?: string;
};
type ResolvedFileTarget = {
  fileId: string;
  element: HTMLElement;
};
function normalizeCandidateSide(value: string | null): FocusCodeSide | null {
  if (value === 'new' || value === 'additions') {
    return 'new';
  }
  if (value === 'old' || value === 'deletions') {
    return 'old';
  }
  return null;
}
function resolveCandidateSide(candidate: HTMLElement): FocusCodeSide | null {
  const lineType = candidate.getAttribute('data-line-type');
  if (lineType === 'change-addition') {
    return 'new';
  }
  if (lineType === 'change-deletion') {
    return 'old';
  }
  const candidateSide = normalizeCandidateSide(candidate.getAttribute('data-side'));
  if (candidateSide) {
    return candidateSide;
  }
  if (candidate.closest('[data-additions]')) {
    return 'new';
  }
  if (candidate.closest('[data-deletions]')) {
    return 'old';
  }
  const ancestorWithSide = candidate.closest('[data-side]');
  if (!(ancestorWithSide instanceof HTMLElement)) {
    return null;
  }
  return normalizeCandidateSide(ancestorWithSide.getAttribute('data-side'));
}
function matchesRequestedSide(candidate: HTMLElement, lineNumber: number, side?: FocusCodeSide): boolean {
  if (!side) {
    return true;
  }
  const line = candidate.getAttribute('data-line');
  const altLine = candidate.getAttribute('data-alt-line');
  const requestedLine = String(lineNumber);
  const lineMatches = line === requestedLine || altLine === requestedLine;
  if (!lineMatches) {
    return false;
  }
  return resolveCandidateSide(candidate) === side;
}
export function FocusCodeBlock(t0) {
  const $ = _c(38);
  const {
    args,
    disableAutoNavigate: t1,
    autoNavigateKey
  } = t0;
  const disableAutoNavigate = t1 === undefined ? false : t1;
  const {
    activeTab,
    switchToChangesTab
  } = usePrTabNavigationStore();
  const navigateToFileInDiff = useDiffNavigationStore(_temp);
  const hasToolError = args.success === false && args.error;
  const [status, setStatus] = useState(disableAutoNavigate ? hasToolError ? "error" : "success" : "pending");
  const normalizeLineNumbers = _temp5;
  let t2;
  if ($[0] !== args.lineNumber || $[1] !== args.lineNumbers) {
    const getLinesToHighlight = () => {
      if (args.lineNumbers) {
        return normalizeLineNumbers(args.lineNumbers);
      }
      if (args.lineNumber) {
        return normalizeLineNumbers([args.lineNumber]);
      }
      return [];
    };
    t2 = getLinesToHighlight();
    $[0] = args.lineNumber;
    $[1] = args.lineNumbers;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const linesToHighlight = t2;
  const resolvedStatus = disableAutoNavigate ? hasToolError ? "error" : "success" : status;
  let t3;
  if ($[3] !== autoNavigateKey) {
    t3 = () => {
      if (autoNavigateKey) {
        completedFocusCodeAutoNavigateKeys.add(autoNavigateKey);
      }
    };
    $[3] = autoNavigateKey;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const markAutoNavigationComplete = t3;
  const scrollToFile = _temp6;
  const resolveFileTarget = _temp7;
  const scrollToLine = _temp10;
  let highlightRequestedLines;
  if ($[5] !== args.side || $[6] !== linesToHighlight) {
    highlightRequestedLines = (fileId_1, fileElement_0, retryTimeoutIds, onComplete, t4) => {
      const attempt = t4 === undefined ? 0 : t4;
      const lines_0 = linesToHighlight;
      if (lines_0.length === 0) {
        return;
      }
      let scrollSuccess = false;
      for (const line of lines_0) {
        if (scrollToLine(fileId_1, line, args.side)) {
          scrollSuccess = true;
          break;
        }
      }
      if (!scrollSuccess && attempt < LINE_SCROLL_RETRY_MAX_ATTEMPTS) {
        const retryId = window.setTimeout(() => {
          highlightRequestedLines(fileId_1, fileElement_0, retryTimeoutIds, onComplete, attempt + 1);
        }, LINE_SCROLL_RETRY_DELAY);
        retryTimeoutIds.push(retryId);
        return;
      }
      if (!scrollSuccess) {
        scrollToFile(fileElement_0);
      }
      if (lines_0.length > 1) {
        highlightLines(fileId_1, lines_0, args.side);
      } else {
        if (lines_0[0] !== undefined) {
          highlightLine(fileId_1, lines_0[0], args.side);
        }
      }
      onComplete();
    };
    $[5] = args.side;
    $[6] = linesToHighlight;
    $[7] = highlightRequestedLines;
  } else {
    highlightRequestedLines = $[7];
  }
  let t4;
  if ($[8] !== activeTab || $[9] !== args.filename || $[10] !== highlightRequestedLines || $[11] !== linesToHighlight || $[12] !== markAutoNavigationComplete || $[13] !== navigateToFileInDiff || $[14] !== switchToChangesTab) {
    t4 = () => {
      const needsTabSwitch = activeTab !== PR_TABS.CHANGES;
      if (needsTabSwitch) {
        switchToChangesTab();
      }
      const tabSwitchDelay = needsTabSwitch ? TAB_SWITCH_DELAY : 0;
      const highlightDelay = needsTabSwitch ? LINE_HIGHLIGHT_DELAY_WITH_TAB : LINE_HIGHLIGHT_DELAY_NO_TAB;
      let cancelled = false;
      let attempts = 0;
      let timeoutId = null;
      const lineTimeoutIds = [];
      const tryNavigate = () => {
        if (cancelled) {
          return;
        }
        const lines_1 = linesToHighlight;
        const didQueueNavigation = navigateToFileInDiff(args.filename, {
          source: "programmatic",
          expandFile: true,
          ...(lines_1.length > 0 ? {
            suppressScroll: true
          } : {})
        });
        const fileTarget = resolveFileTarget(args.filename);
        if (!fileTarget) {
          attempts = attempts + 1;
          attempts;
          if (attempts >= NAVIGATION_RETRY_MAX_ATTEMPTS) {
            console.error("File element not found:", {
              filename: args.filename,
              candidateIds: getCandidateDiffFileElementIds(args.filename)
            });
            setStatus("error");
            return;
          }
          timeoutId = window.setTimeout(tryNavigate, Math.min(NAVIGATION_RETRY_BASE_DELAY * attempts, NAVIGATION_RETRY_MAX_DELAY));
          return;
        }
        const {
          element: fileElement_1,
          fileId: fileId_2
        } = fileTarget;
        if (lines_1.length > 0) {
          const lineNavigationTimeout = window.setTimeout(() => highlightRequestedLines(fileId_2, fileElement_1, lineTimeoutIds, markAutoNavigationComplete), highlightDelay);
          lineTimeoutIds.push(lineNavigationTimeout);
        } else {
          if (!didQueueNavigation) {
            scrollToFile(fileElement_1);
            markAutoNavigationComplete();
          } else {
            markAutoNavigationComplete();
          }
        }
        setStatus("success");
      };
      timeoutId = window.setTimeout(tryNavigate, tabSwitchDelay);
      return () => {
        cancelled = true;
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
        }
        lineTimeoutIds.forEach(_temp11);
      };
    };
    $[8] = activeTab;
    $[9] = args.filename;
    $[10] = highlightRequestedLines;
    $[11] = linesToHighlight;
    $[12] = markAutoNavigationComplete;
    $[13] = navigateToFileInDiff;
    $[14] = switchToChangesTab;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const handleScrollToFile = useEventCallback(t4);
  let t5;
  let t6;
  if ($[16] !== autoNavigateKey || $[17] !== disableAutoNavigate || $[18] !== handleScrollToFile || $[19] !== hasToolError) {
    t5 = () => {
      if (disableAutoNavigate) {
        return;
      }
      if (hasToolError) {
        queueMicrotask(() => {
          setStatus("error");
        });
        return;
      }
      if (autoNavigateKey && completedFocusCodeAutoNavigateKeys.has(autoNavigateKey)) {
        queueMicrotask(() => {
          setStatus("success");
        });
        return;
      }
      const cleanup = handleScrollToFile();
      return () => {
        cleanup?.();
        clearHighlights();
      };
    };
    t6 = [autoNavigateKey, disableAutoNavigate, handleScrollToFile, hasToolError];
    $[16] = autoNavigateKey;
    $[17] = disableAutoNavigate;
    $[18] = handleScrollToFile;
    $[19] = hasToolError;
    $[20] = t5;
    $[21] = t6;
  } else {
    t5 = $[20];
    t6 = $[21];
  }
  useEffect(t5, t6);
  let t7;
  if ($[22] !== args.error || $[23] !== args.filename || $[24] !== args.side || $[25] !== hasToolError || $[26] !== linesToHighlight || $[27] !== resolvedStatus) {
    t7 = () => {
      if (hasToolError && args.error) {
        return args.error;
      }
      switch (resolvedStatus) {
        case "pending":
          {
            return "Navigating to file...";
          }
        case "success":
          {
            if (linesToHighlight.length > 1) {
              const first = linesToHighlight[0];
              const last = linesToHighlight[linesToHighlight.length - 1];
              const isContiguous = first !== undefined && last !== undefined && last - first + 1 === linesToHighlight.length;
              if (isContiguous && first !== undefined && last !== undefined) {
                return `Navigated to ${args.filename}${args.side ? ` (${args.side})` : ""}:${first}-${last}`;
              }
              return `Navigated to ${args.filename}${args.side ? ` (${args.side})` : ""}:${linesToHighlight.join(",")}`;
            }
            if (linesToHighlight.length === 1 && linesToHighlight[0] !== undefined) {
              return `Navigated to ${args.filename}${args.side ? ` (${args.side})` : ""}:${linesToHighlight[0]}`;
            }
            return `Navigated to ${args.filename}`;
          }
        case "error":
          {
            return `Could not find ${args.filename}`;
          }
      }
    };
    $[22] = args.error;
    $[23] = args.filename;
    $[24] = args.side;
    $[25] = hasToolError;
    $[26] = linesToHighlight;
    $[27] = resolvedStatus;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  const getStatusMessage = t7;
  let t8;
  if ($[29] !== resolvedStatus) {
    t8 = resolvedStatus === "pending" && <Loader2 size={12} className="animate-spin" />;
    $[29] = resolvedStatus;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== getStatusMessage) {
    t9 = getStatusMessage();
    $[31] = getStatusMessage;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] !== t9) {
    t10 = <span>{t9}</span>;
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== t10 || $[36] !== t8) {
    t11 = <div className="flex w-full items-center gap-2 overflow-hidden py-1 text-xs text-zinc-400">{t8}{t10}</div>;
    $[35] = t10;
    $[36] = t8;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  return t11;
}
function _temp11(lineTimeoutId) {
  clearTimeout(lineTimeoutId);
}
function _temp10(fileId_0, lineNumber, side) {
  const possibleIds = side === "new" ? [`${fileId_0}-line-new-${lineNumber}`, `${fileId_0}-line-${lineNumber}-${lineNumber}`] : side === "old" ? [`${fileId_0}-line-old-${lineNumber}`, `${fileId_0}-line-${lineNumber}-${lineNumber}`] : [`${fileId_0}-line-new-${lineNumber}`, `${fileId_0}-line-${lineNumber}-${lineNumber}`, `${fileId_0}-line-old-${lineNumber}`];
  for (const id of possibleIds) {
    const lineEl = document.getElementById(id);
    if (lineEl instanceof HTMLElement) {
      lineEl.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
      return true;
    }
  }
  const fileElement = document.getElementById(fileId_0);
  if (fileElement instanceof HTMLElement) {
    const v2Candidates = Array.from(fileElement.querySelectorAll(`[data-line="${lineNumber}"], [data-alt-line="${lineNumber}"], [data-line-number="${lineNumber}"], [data-column-number="${lineNumber}"]`)).filter(_temp8);
    const v2Line = v2Candidates.find(candidate_0 => matchesRequestedSide(candidate_0, lineNumber, side));
    if (v2Line instanceof HTMLElement) {
      v2Line.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
      return true;
    }
    const diffContainers = fileElement.querySelectorAll("diffs-container");
    for (const container of diffContainers) {
      if (!(container instanceof HTMLElement) || !container.shadowRoot) {
        continue;
      }
      const shadowCandidates = Array.from(container.shadowRoot.querySelectorAll(`[data-line="${lineNumber}"], [data-alt-line="${lineNumber}"], [data-line-number="${lineNumber}"], [data-column-number="${lineNumber}"]`)).filter(_temp9);
      const shadowLine = shadowCandidates.find(candidate_2 => matchesRequestedSide(candidate_2, lineNumber, side));
      if (shadowLine instanceof HTMLElement) {
        shadowLine.scrollIntoView({
          behavior: "smooth",
          block: "center"
        });
        return true;
      }
    }
  }
  return false;
}
function _temp9(candidate_1) {
  return candidate_1 instanceof HTMLElement;
}
function _temp8(candidate) {
  return candidate instanceof HTMLElement;
}
function _temp7(filename) {
  for (const fileId of getCandidateDiffFileElementIds(filename)) {
    const element_0 = document.getElementById(fileId);
    if (element_0 instanceof HTMLElement) {
      return {
        fileId,
        element: element_0
      };
    }
  }
  return null;
}
function _temp6(element) {
  element.scrollIntoView({
    behavior: "smooth",
    block: "start"
  });
  window.scrollBy(0, -NAVBAR_OFFSET);
  return true;
}
function _temp5(lines) {
  const sanitized = Array.from(new Set(lines.map(_temp2).filter(_temp3))).sort(_temp4);
  if (sanitized.length === 2) {
    const [start, end] = sanitized;
    if (start !== undefined && end !== undefined && end - start > 1) {
      const rangeLength = end - start + 1;
      if (rangeLength <= MAX_EXPANDED_LINE_RANGE) {
        return Array.from({
          length: rangeLength
        }, (_, index) => start + index);
      }
    }
  }
  return sanitized;
}
function _temp4(a, b) {
  return a - b;
}
function _temp3(value_0) {
  return Number.isFinite(value_0) && value_0 > 0;
}
function _temp2(value) {
  return Math.trunc(value);
}
function _temp(s) {
  return s.navigateToFile;
}